import React, { useEffect, useMemo, useState } from "react";

const expectedHeaders = [
  "first_name",
  "last_name",
  "emp_id",
  "phone",
  "L1_first_name",
  "L1_last_name",
  "L1_emp_id",
  "L1_phone",
  "L2_first_name",
  "L2_last_name",
  "L2_emp_id",
  "L2_phone",
];

const TeamsValidationStep = ({ csvData, handleValidationResult, schoolId }) => {
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  const validateHierarchyUpload = (data) => {
    const errors = [];
    const validData = [];

    data.forEach((row, index) => {
      const errorMessages = [];
      const {
        first_name,
        last_name,
        emp_id,
        phone,
        L1_first_name,
        L1_last_name,
        L1_emp_id,
        L1_phone,
        L2_first_name,
        L2_last_name,
        L2_emp_id,
        L2_phone,
      } = row;

      // Check required fields
      const requiredFields = [
        { field: first_name, name: "First Name" },
        { field: last_name, name: "Last Name" },
        { field: emp_id, name: "Employee ID" },
        { field: phone, name: "Phone" },
        { field: L1_first_name, name: "L1 First Name" },
        { field: L1_last_name, name: "L1 Last Name" },
        { field: L1_emp_id, name: "L1 Employee ID" },
        { field: L1_phone, name: "L1 Phone" },
        { field: L2_first_name, name: "L2 First Name" },
        { field: L2_last_name, name: "L2 Last Name" },
        { field: L2_emp_id, name: "L2 Employee ID" },
        { field: L2_phone, name: "L2 Phone" },
      ];

      requiredFields.forEach(({ field, name }) => {
        if (!field || field.trim() === "") {
          errorMessages.push(`${name} is required`);
        }
      });

      if (emp_id && !/^[a-zA-Z0-9]+$/.test(emp_id)) {
        errorMessages.push(
          "Employee ID must contain only letters and numbers (no spaces or special characters)"
        );
      }

      if (L1_emp_id && !/^[a-zA-Z0-9]+$/.test(L1_emp_id)) {
        errorMessages.push(
          "L1 Employee ID must contain only letters and numbers (no spaces or special characters)"
        );
      }

      if (L2_emp_id && !/^[a-zA-Z0-9]+$/.test(L2_emp_id)) {
        errorMessages.push(
          "L2 Employee ID must contain only letters and numbers (no spaces or special characters)"
        );
      }

      const nameFields = [
        { field: first_name, name: "First Name" },
        { field: last_name, name: "Last Name" },
        { field: L1_first_name, name: "L1 First Name" },
        { field: L1_last_name, name: "L1 Last Name" },
        { field: L2_first_name, name: "L2 First Name" },
        { field: L2_last_name, name: "L2 Last Name" },
      ];

      nameFields.forEach(({ field, name }) => {
        if (field && !/^[A-Za-z\s]+$/.test(field)) {
          errorMessages.push(`${name} must contain only letters and spaces`);
        }
      });

      if (!phone) {
        errorMessages.push("Phone number is required");
      } else {
        if (!/^\d{10}$/.test(phone)) {
          errorMessages.push("Phone numbers must be exactly 10 digits");
        }
      }

      if (!L1_phone) {
        errorMessages.push("L1 Phone number is required");
      } else {
        if (!/^\d{10}$/.test(L1_phone)) {
          errorMessages.push("L1 Phone numbers must be exactly 10 digits");
        }
      }

      if (!L2_phone) {
        errorMessages.push("L2 Phone number is required");
      } else {
        if (!/^\d{10}$/.test(L2_phone)) {
          errorMessages.push("L2 Phone numbers must be exactly 10 digits");
        }
      }

      if (errorMessages.length > 0) {
        errors.push({ index, errors: errorMessages, data: row });
      } else {
        validData.push(row);
      }
    });

    return { validData, errors };
  };

  useEffect(() => {
    if (!csvData || csvData.length === 0) {
      setIsValidating(false);
      setIsValid(false);
      handleValidationResult([], []);
      return;
    }

    if (csvData.length > 0) {
      const uploadedHeaders = Object.keys(csvData[0]);
      const missingHeaders = expectedHeaders.filter(
        (header) => !uploadedHeaders.includes(header)
      );

      if (missingHeaders.length > 0) {
        const headerErrors = csvData.map((row, index) => ({
          index,
          errors: [
            `Incorrect CSV format. Missing required columns: ${missingHeaders.join(
              ", "
            )}. Expected columns: ${expectedHeaders.join(", ")}`,
          ],
          data: row,
        }));

        const timer = setTimeout(() => {
          setIsValidating(false);
          setIsValid(false);
          setValidationErrors(headerErrors);
          handleValidationResult([], headerErrors);
        }, 1000);

        return () => clearTimeout(timer);
      }
    }

    const { validData, errors } = validateHierarchyUpload(csvData);

    const timer = setTimeout(() => {
      setIsValidating(false);
      setIsValid(errors.length === 0);
      setValidationErrors(errors);
      handleValidationResult(validData, errors);
    }, 1000);

    return () => clearTimeout(timer);
  }, [csvData, handleValidationResult]);

  const errorsCount = validationErrors.length;

  const summary = useMemo(() => {
    if (isValidating) {
      return {
        message: "Scanning in progress…",
        tone: "pending",
      };
    }

    if (isValid) {
      return {
        message: "File is clean to upload.",
        tone: "success",
      };
    }

    if (errorsCount > 0) {
      return {
        message: `We found ${errorsCount} error${
          errorsCount === 1 ? "" : "s"
        } in the file. Select “View” to inspect each row.`,
        tone: "error",
      };
    }

    return {
      message: "File has errors. Select “View” to inspect each row.",
      tone: "error",
    };
  }, [errorsCount, isValid, isValidating]);

  return (
    <section
      className="validation-step"
      aria-live="polite"
      aria-busy={isValidating}
    >
      <h2 className="validation-step__title">Validating CSV Data</h2>
      <p
        className={`validation-step__status${
          summary.tone === "success"
            ? " validation-step__status--success"
            : summary.tone === "error"
            ? " validation-step__status--error"
            : ""
        }`.trim()}
      >
        {summary.message}
      </p>
    </section>
  );
};

export default TeamsValidationStep;
